import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialPostPageHeader.css';
import { FaEllipsisH, FaFlag, FaTrashAlt, FaRegCopy, FaPen } from 'react-icons/fa';
import { CustomImage } from "./CustomImage.js";
import { useNavigate } from "./Router";

const SocialPostPageHeader = ({ page, pageId, underMessage, post, canDelete = false, onDelete, canEdit = false, onEdit }) => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState("");
  const [pageName, setPageName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [user, setUser] = useState(null);
  
  const optionsRef = useRef(null);

  const clickPageBy = () => {
    navigate(`/page/${page ? page.page_id : pageId}`);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowOptions(false);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
    setShowOptions(false);
  };

  const copyLink = () => {
    if (!post) return;
    
    const link = `https://peupleqc.com/post/${post.id}${user ? `?ref=${user.user_id}` : ''}`;
    navigator.clipboard.writeText(link)
      .then(() => {
        alert("Lien copié avec succès!");
      })
      .catch(err => {
        console.error('Erreur lors de la copie: ', err);
      });
    
    setShowOptions(false);
  };

  const reportPost = () => {
    sdk.models.post.signal({id: post.id});
    alert("Merci, nous allons regarder ça.");
    setShowOptions(false);
  };

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("liberium-token");
        if (token) {
          const myself = localStorage.getItem("myself");
          if (myself) {
            setUser(JSON.parse(myself));
          } else {
            const response = await sdk.models.PeupleUser.getMySelf({});
            if (response && !response.error) {
              setUser(response);
              localStorage.setItem("myself", JSON.stringify(response));
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'utilisateur actuel:", err);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch page data
  useEffect(() => {
    const fetchPageData = async () => {
      setIsLoading(true);
      try {
        const pageResponse = await sdk.models.SocialPages.getOne(pageId || page.page_id);
        if (pageResponse) {
          setAvatarUrl(pageResponse.avatar 
            ? `https://peupleqc.com/s/peupleqc-upload/${pageResponse.avatar}` 
            : "https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
          setPageName(pageResponse.page_title || "Page sans nom");
          setError("");
        } else {
          setError("Page non trouvée");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des données de la page:", err);
        setError("Erreur lors du chargement");
      } finally {
        setIsLoading(false);
      }
    };

    if (pageId || page) {
      fetchPageData();
    }
  }, [pageId, page]);

  // Close options menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptions && optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showOptions]);

  if (error) {
    return <div className="spp-error">{error}</div>;
  }

  return (
    <div className="spp-header">
      <div className="spp-profile" onClick={clickPageBy}>
        <div className="spp-avatar">
          {isLoading ? (
            <div className="spp-avatar-placeholder"></div>
          ) : (
            <CustomImage 
              src={avatarUrl} 
              className="spp-avatar-image" 
              alt={pageName || "Page"} 
            />
          )}
        </div>
        
        <div className="spp-info">
          <div className="spp-name-container">
            <h4 className="spp-name">
              {pageName}
            </h4>
          </div>
          
          {underMessage && (
            <div className="spp-meta">
              <span className="spp-context">{underMessage}</span>
            </div>
          )}
        </div>
      </div>

      <div className="spp-options" ref={optionsRef}>
        <div 
          className="spp-options-trigger" 
          onClick={(e) => {
            e.stopPropagation();
            setShowOptions(!showOptions);
          }}
          aria-label="Options de publication"
        >
          <FaEllipsisH />
        </div>

        {showOptions && (
          <div className="spp-options-menu">
            {canEdit && (
              <div className="spp-menu-item" onClick={handleEdit}>
                <FaPen className="spp-menu-item-icon" />
                Modifier
              </div>
            )}
            
            <div className="spp-menu-item" onClick={copyLink}>
              <FaRegCopy className="spp-menu-item-icon" />
              Copier le lien
            </div>
            
            <div className="spp-menu-item" onClick={reportPost}>
              <FaFlag className="spp-menu-item-icon" />
              Signaler
            </div>
            
            {canDelete && (
              <div className="spp-menu-item spp-menu-item-delete" onClick={handleDelete}>
                <FaTrashAlt className="spp-menu-item-icon" />
                Supprimer
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { SocialPostPageHeader };